import type { Role } from '@/types';

import { isFeatureEnabled } from './preview-features';

/**
 * Search-param key naming the installation request whose detail drawer is open. A cross-page
 * hand-off (the Dashboard's quick-action row click → the Requests queue), so it needs a channel that
 * survives a reload and a paste (grilling Q10 of feature 112393). Homed here beside the path it
 * qualifies: the two spellers are a page and a feature, and `config` is the only layer both may
 * import (ADR-0010).
 */
export const INSTALLATION_DETAIL_PARAM = 'request';

export const routes = {
  login: '/login',
  loginVerifyOtp: '/login/verify-otp',
  loginSetPassword: '/login/set-password',
  forgotPassword: '/forgot-password',
  forgotVerify: '/forgot-password/verify',
  forgotReset: '/forgot-password/reset',

  dashboard: '/',
  // Destination for roles with no permitted module (ADR-0028): Field Installer, Dealer (outside the
  // `dealers` release scope — Amendment 1), and a session whose wire role this build does not
  // recognise (ADR-0021).
  noModules: '/no-modules',

  orders: '/orders',
  order: (id = ':id') => `/orders/${id}`,
  orderEdit: (id = ':id') => `/orders/${id}/edit`,

  customers: '/customers',
  customerCreate: '/customers/new',
  customer: (id = ':id') => `/customers/${id}`,
  customerEdit: (id = ':id') => `/customers/${id}/edit`,
  customerOrderHistory: (id = ':id') => `/customers/${id}/order-history`,
  customerDevices: (id = ':id') => `/customers/${id}/devices`,
  customerDeviceAlerts: (id = ':id') => `/customers/${id}/device-alerts`,
  customerSubscriptions: (id = ':id') => `/customers/${id}/subscription-details`,
  customerOrderSupport: (id = ':id') => `/customers/${id}/order-support`,
  customerDeviceSupport: (id = ':id') => `/customers/${id}/device-support`,
  customerActivityLog: (id = ':id') => `/customers/${id}/activity-log`,
  customerMap: (id = ':id') => `/customers/${id}/map`,
  /**
   * One Customer's Device (feature 119076 ticket 03). A **sibling** of the `/customers/:id` hub,
   * not a child: Figma `1950:869003` draws its own `‹ Back` with no customer header and no tab
   * bar, the same shape `/customers/:id/edit` already takes. The Customer is in the path because
   * every remap request is scoped by it — which is what makes 119177 §1b's "scoped to the Customer
   * whose device I opened" structural rather than something each call site must remember.
   * Replaces `routes.device(id)`, the product-catalog route the Devices tab used to feed a
   * `customerDeviceId` into.
   */
  customerDevice: (id = ':id', deviceId = ':deviceId') => `/customers/${id}/devices/${deviceId}`,
  /**
   * Remap Device (119177 §1b, §2a). A hub **child**, unlike `customerDevice` above: its frames are
   * the *expanded* state of a surface that also has a normal one (the `CornersIn` collapse glyph in
   * `2039:512886`), so `<ModuleTabs>` keeps **Devices** lit — which is where the admin came from and
   * where Cancel returns (grilling Q3; locked decision 3).
   */
  customerDeviceRemap: (id = ':id', deviceId = ':deviceId') =>
    `/customers/${id}/devices/${deviceId}/remap`,
  /**
   * Field Mapping, on its own route inside the Customer hub (grilling Q4; locked decision 4). A
   * **sibling** of `customerMap`, not a child: the hub keeps the Customer's name in the page header
   * and `<ModuleTabs>` keeps the Map tab active off the `map/` prefix, while the Map view unmounts so
   * only one `<MapCanvas>` is ever live. Routing rather than in-page state is also what makes the
   * discard guard buildable — ticket 13's `useBlocker` covers "navigate to another section", which
   * in-page state cannot intercept.
   */
  customerFieldNew: (id = ':id') => `/customers/${id}/map/fields/new`,
  /**
   * Ticket 16's twin of `customerFieldNew` — the *same* Field Mapping screen, seeded from a saved
   * Field Area rather than opening empty. A sibling of `map` for the same reasons, and a sibling of
   * `map/fields/new` so both reach one page component.
   */
  customerFieldEdit: (id = ':id', fieldId = ':fieldId') =>
    `/customers/${id}/map/fields/${fieldId}/edit`,

  discounts: '/discounts',
  discountCreate: '/discounts/create',
  discount: (id = ':id') => `/discounts/${id}`,

  products: '/products',
  productEdit: (id = ':id') => `/products/${id}/edit`,
  bundles: '/products/bundles',
  productsBundlesNew: '/products/bundles/new',
  productsBundlesEdit: (id = ':id') => `/products/bundles/${id}/edit`,
  // products hub tabs
  productsDevices: '/products/devices',
  productsComponents: '/products/components',
  productsComponentsNew: '/products/components/new',
  productsComponentsEdit: (id = ':id') => `/products/components/${id}/edit`,
  productsDevicesNew: '/products/devices/new',
  productsDevicesEdit: (id = ':id') => `/products/devices/${id}/edit`,
  productsCategory: '/products/category',

  // request hub
  requests: '/requests',
  requestsInstallation: '/requests/installation',
  requestsInstallationDetail: (orderId: string) => `/requests/installation?request=${orderId}`,
  requestsInstallationMap: (id = ':id') => `/requests/installation/${id}/map`,
  requestsReplacement: '/requests/replacement',
  requestsWarranty: '/requests/warranty',

  devices: '/devices',
  device: (id = ':id') => `/devices/${id}`,

  support: '/support',
  ticket: (id = ':id') => `/support/${id}`,
  // support ticket hub tabs
  supportOrders: '/support/orders',
  supportDevices: '/support/devices',

  subscriptions: '/subscriptions',
  payments: '/payments',

  reports: '/reports',
  reportOrderSummary: '/reports/order-summary',
  reportProducts: '/reports/products',
  reportDeviceActivity: '/reports/device-activity',
  reportSubscriptions: '/reports/subscriptions',

  notifications: '/notifications',
  activityLog: '/activity-log',

  configurations: '/configurations',
  configTax: '/configurations/tax',
  configInstallationFees: '/configurations/installation-fees',
  configWarrantyPeriods: '/configurations/warranty-periods',

  adminUsers: '/admin-users',
  // User Management hub tabs (feature 118478, ADR-0036) — only registered while `dealers` is on;
  // with it off `/admin-users` is still the single Internal Users page.
  adminUsersInternal: '/admin-users/internal',
  dealers: '/admin-users/dealers',
  /** Keyed on the Dealer's `admin_users.id` (ADR-0036). Full-page, beside the hub (no hub tabs). */
  dealer: (id = ':dealerId') => `/admin-users/dealers/${id}`,
  dealerUnassigned: (id = ':dealerId') => `/admin-users/dealers/${id}/unassigned`,
  dealerSelfAssigned: (id = ':dealerId') => `/admin-users/dealers/${id}/self-assigned`,
  dealerSold: (id = ':dealerId') => `/admin-users/dealers/${id}/sold`,

  // The dealer's own portal (feature 120817, ADO 120818 §G) — only registered while `dealers` is
  // on. A `/dealer` prefix rather than the admin module paths: a dealer's Orders, Customers and
  // Devices are their own scoped listings (`dealer-orders`, `dealer-customers`, `dealer-devices`
  // on the API), not the admin pages with a filter, so each admin path keeps refusing a dealer.
  dealerPortalDevices: '/dealer/devices',
  dealerPortalOrders: '/dealer/orders',
  dealerPortalOrder: (id = ':id') => `/dealer/orders/${id}`,
  dealerPortalProducts: '/dealer/products',
  // Product Management tabs for the dealer (feature 122142) — the same four as `/products/*`.
  dealerPortalProductsDevices: '/dealer/products/devices',
  dealerPortalProductsComponents: '/dealer/products/components',
  dealerPortalProductsCategory: '/dealer/products/category',
  dealerPortalProductsBundles: '/dealer/products/bundles',
  dealerPortalCustomers: '/dealer/customers',
  // Dealer Devices (feature 122138, grilling G1/G10). Tabs are siblings under the landing route;
  // the detail is a full-page sibling of the tabs; the customer page is reserved for 121599.
  dealerPortalDevicesUnassigned: '/dealer/devices/unassigned',
  dealerPortalDevicesMine: '/dealer/devices/my-devices',
  dealerPortalDevicesSold: '/dealer/devices/sold',
  dealerPortalDevice: (id = ':deviceId') => `/dealer/devices/my-devices/${id}`,
  dealerPortalCustomerDevices: (id = ':id') => `/dealer/customers/${id}/devices`,

  notFound: '/404',
  serverError: '/500',
} as const;

/**
 * Where each role belongs (ADR-0028, which amends ADR-0014). Three consumers: the `/` index route,
 * `RequireRole`'s miss branch, and — as a hand-kept literal — the Dashboard nav entry.
 *
 * Typed `Record<Role, string>` on purpose: adding a member to the `Role` union is then a compile
 * error here rather than a silent fallthrough to an undefined path.
 *
 * The duplication against each module's `*_ROLES` constant is unavoidable — `config` may not import
 * from `features` under eslint-plugin-boundaries, which is the same reason `nav.tsx` carries its
 * role lists as literals with a matching comment.
 */
const LANDING_ROUTES: Record<Role, string> = {
  super_admin: routes.dashboard,
  admin: routes.dashboard,
  operational_associate: routes.orders,
  field_installer: routes.noModules,
  // Overridden by `resolveLandingRoute` while `dealers` is on: a dealer lands on their Device
  // listing (ADO 120818 §G). Outside the release scope they keep landing on No modules.
  dealer: routes.noModules,
};

/**
 * A nullish role is a session whose wire role this build does not recognise, degraded to least
 * privilege by ADR-0021. It is denied every guarded route and the Dashboard, so "no modules
 * available" is the literal truth for it.
 */
export function resolveLandingRoute(role: Role | null | undefined): string {
  if (role === 'dealer' && isFeatureEnabled('dealers')) return routes.dealerPortalDevices;
  return role ? LANDING_ROUTES[role] : routes.noModules;
}
